import { useEffect, useState } from "react";
import { Trash2, Plus, Save, LogOut } from "lucide-react";
import { toast } from "sonner";
import api, { adminHeaders, adminToken } from "../lib/api";

const EMPTY = {
  slug: "", title: "", designer: "", designer_slug: "", year: 1950, era: "", movement: "",
  movement_slug: "", materials: "", style: "", image_url: "", history: "",
  source_name: "", source_url: "", license: "", featured: false, published: true,
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!adminToken());
  const [pw, setPw] = useState("");
  const [chairs, setChairs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);

  const load = () => api.get("/admin/chairs", { headers: adminHeaders() })
    .then((r) => setChairs(r.data))
    .catch(() => { localStorage.removeItem("ci_admin_token"); setAuthed(false); });

  useEffect(() => { if (authed) load(); }, [authed]); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/login", { password: pw });
      localStorage.setItem("ci_admin_token", r.data.token);
      setAuthed(true);
      toast.success("Welcome, curator");
    } catch { toast.error("Invalid password"); }
  };

  const logout = () => { localStorage.removeItem("ci_admin_token"); setAuthed(false); };

  const edit = (c) => {
    setForm({ ...c, materials: (c.materials || []).join(", ") });
    setEditing(true);
    window.scrollTo(0, 0);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, year: Number(form.year), materials: form.materials.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/chairs/${form.slug}`, payload, { headers: adminHeaders() });
      else await api.post("/admin/chairs", payload, { headers: adminHeaders() });
      toast.success(editing ? "Chair updated" : "Chair published");
      setForm(EMPTY); setEditing(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this chair?")) return;
    await api.delete(`/admin/chairs/${slug}`, { headers: adminHeaders() });
    toast.success("Deleted"); load();
  };

  const field = (k, label, type = "text") => (
    <label className="block">
      <span className="overline">{label}</span>
      <input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-border bg-background h-10 px-3 mt-1 text-sm" data-testid={`admin-field-${k}`} />
    </label>
  );

  if (!authed) return (
    <div className="max-w-sm mx-auto px-5 pt-32">
      <h1 className="serif text-4xl mb-6">Curator login</h1>
      <form onSubmit={login} className="space-y-4">
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="w-full border border-border bg-card h-12 px-4 text-sm" data-testid="admin-password" />
        <button className="w-full bg-primary text-primary-foreground h-12 text-sm font-medium" data-testid="admin-login-btn">Enter</button>
      </form>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="overline text-accent">Curation</p>
          <h1 className="serif text-4xl sm:text-5xl tracking-tight">Admin</h1>
        </div>
        <button onClick={logout} className="inline-flex items-center gap-2 text-sm border border-border h-10 px-4" data-testid="admin-logout"><LogOut size={15} /> Log out</button>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10">
        <form onSubmit={save} className="border border-border bg-card p-6 space-y-4 self-start lg:sticky lg:top-20">
          <h2 className="serif text-2xl">{editing ? `Editing: ${form.title}` : "Add a chair"}</h2>
          <div className="grid grid-cols-2 gap-3">
            {field("slug", "Slug")}
            {field("title", "Title")}
            {field("designer", "Designer")}
            {field("designer_slug", "Designer slug")}
            {field("year", "Year", "number")}
            {field("era", "Era")}
            {field("movement", "Movement")}
            {field("movement_slug", "Movement slug")}
            {field("materials", "Materials (comma-sep)")}
            {field("style", "Style")}
            {field("source_name", "Source name")}
            {field("license", "License")}
          </div>
          {field("image_url", "Image URL")}
          {field("source_url", "Source URL")}
          <label className="block">
            <span className="overline">History</span>
            <textarea value={form.history} onChange={(e) => setForm({ ...form, history: e.target.value })} rows={4} className="w-full border border-border bg-background p-3 mt-1 text-sm" data-testid="admin-field-history" />
          </label>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published</label>
          </div>
          <div className="flex gap-3">
            <button className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-5 h-11 text-sm font-medium" data-testid="admin-save-btn">
              {editing ? <><Save size={15} /> Update</> : <><Plus size={15} /> Publish</>}
            </button>
            {editing && <button type="button" onClick={() => { setForm(EMPTY); setEditing(false); }} className="text-sm underline">Cancel</button>}
          </div>
        </form>

        <div>
          <p className="text-sm text-muted-foreground mb-4">{chairs.length} chairs in catalog</p>
          <div className="space-y-2">
            {chairs.map((c) => (
              <div key={c.slug} className="flex items-center gap-3 border border-border bg-card p-3" data-testid={`admin-row-${c.slug}`}>
                <img src={c.image_url} alt="" className="w-12 h-14 object-cover shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{c.title} {!c.published && <span className="text-xs text-destructive">(draft)</span>}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.designer} · {c.year}</p>
                </div>
                <button onClick={() => edit(c)} className="text-xs underline" data-testid={`admin-edit-${c.slug}`}>Edit</button>
                <button onClick={() => remove(c.slug)} className="text-destructive p-1" data-testid={`admin-delete-${c.slug}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
